// Mirror of `stereo_render::scene` (serde JSON). Keep in sync with Rust.

export type Vec3 = [number, number, number];

export interface Transform {
  position: Vec3;
  /** Degrees, Euler order XYZ. */
  rotation: Vec3;
  scale: Vec3;
}

export interface HeightData {
  cols: number;
  rows: number;
  /** base64 of little-endian u16. */
  samples: string;
}

export type Shape =
  | { kind: 'group' }
  | { kind: 'box'; size: Vec3 }
  | { kind: 'sphere'; radius: number; width_segments: number; height_segments: number }
  | { kind: 'torus'; radius: number; tube: number; radial_segments: number; tubular_segments: number }
  | { kind: 'cylinder'; radius_top: number; radius_bottom: number; height: number; radial_segments: number }
  | { kind: 'plane'; width: number; height: number }
  | { kind: 'mesh'; mesh: string }
  | { kind: 'heightmap'; width: number; height: number; depth: number; data: HeightData }
  | {
      kind: 'terrain';
      width: number;
      height: number;
      depth: number;
      resolution: number;
      scale: number;
      octaves: number;
      seed: number;
    };

export type ShapeKind = Shape['kind'];

export interface SceneNode {
  id: string;
  name: string;
  parent: string | null;
  transform: Transform;
  visible: boolean;
  shape: Shape;
  /** Editor-only data, e.g. text parameters for re-baking. */
  source?: { kind: 'text'; text: Record<string, unknown> } | { kind: 'import'; file: string };
}

export interface MeshData {
  id: string;
  /** base64 of little-endian f32, 9 floats per triangle. */
  positions: string;
}

export type Easing = 'linear' | 'ease_in' | 'ease_out' | 'ease_in_out' | 'step';

export interface Key {
  time: number;
  position?: Vec3;
  rotation?: Vec3;
  scale?: Vec3;
  target?: Vec3;
  easing?: Easing;
}

export interface Track {
  target: string;
  keys: Key[];
}

export interface Camera {
  projection: 'perspective' | 'orthographic';
  position: Vec3;
  target: Vec3;
  fov: number;
  height: number;
  near: number;
  far: number;
  /** Output depth at far / near (default 0 / 1; background stays 0). */
  base?: number;
  top?: number;
}

export interface SceneDesc {
  version: number;
  camera: Camera;
  nodes: SceneNode[];
  meshes: MeshData[];
  timeline: { duration: number; tracks: Track[] };
}

export const CAMERA_TRACK = 'camera';

export function defaultTransform(): Transform {
  return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] };
}

export function emptyScene(): SceneDesc {
  return {
    version: 1,
    camera: {
      projection: 'perspective',
      position: [0, 0, 10],
      target: [0, 0, 0],
      fov: 35,
      height: 6,
      near: 7,
      far: 13,
    },
    nodes: [],
    meshes: [],
    timeline: { duration: 4, tracks: [] },
  };
}

export function defaultShape(kind: ShapeKind): Shape {
  switch (kind) {
    case 'group':
      return { kind };
    case 'box':
      return { kind, size: [1.5, 1.5, 1.5] };
    case 'sphere':
      return { kind, radius: 1, width_segments: 48, height_segments: 24 };
    case 'torus':
      return { kind, radius: 1.2, tube: 0.4, radial_segments: 24, tubular_segments: 64 };
    case 'cylinder':
      return { kind, radius_top: 0.8, radius_bottom: 0.8, height: 2, radial_segments: 48 };
    case 'plane':
      return { kind, width: 4, height: 3 };
    case 'terrain':
      return { kind, width: 6, height: 4, depth: 1, resolution: 128, scale: 24, octaves: 5, seed: 1 };
    case 'heightmap':
      return { kind, width: 6, height: 4, depth: 1, data: { cols: 2, rows: 2, samples: 'AAAAAAAAAAA=' } };
    case 'mesh':
      return { kind, mesh: '' };
  }
}

// ---- base64 helpers ------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function f32ToBase64(a: Float32Array): string {
  return bytesToBase64(new Uint8Array(a.buffer, a.byteOffset, a.byteLength));
}

export function base64ToF32(b64: string): Float32Array {
  const b = base64ToBytes(b64);
  return new Float32Array(b.buffer, b.byteOffset, b.byteLength / 4);
}

export function u16ToBase64(a: Uint16Array): string {
  return bytesToBase64(new Uint8Array(a.buffer, a.byteOffset, a.byteLength));
}

export function newId(prefix = 'n'): string {
  return `${prefix}${Math.random().toString(36).slice(2, 9)}`;
}
